/**
 * Directory accounts: companies we invoice or are billed by who are not customers.
 * The nature of the work (partner trade, tenant …) is set on each arrangement, not here,
 * because one company can be both.
 */

/** HMRC CIS band. `none` means the account is not a CIS subcontractor. */
export type CisBand = 'none' | 'gross' | 'standard' | 'higher';

export const CIS_BAND_LABELS: Record<CisBand, string> = {
  none: 'Not CIS',
  gross: 'Gross (0%)',
  standard: 'Standard (20%)',
  higher: 'Higher (30%)',
};

export const CIS_BAND_RATES: Record<CisBand, number | null> = {
  none: null,
  gross: 0,
  standard: 0.2,
  higher: 0.3,
};

/** Bank and identity details printed on a subcontractor's bills. */
export type SubcontractorDetails = {
  niNumber: string | null;
  bankName: string | null;
  bankSortCode: string | null;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  logoUrl: string | null;
};

/** When we pay the account's bills; sets the due date on new bills. */
export type BillPaymentTerms = 'none' | 'weekly_same_friday' | 'weekly_next_friday';

export const BILL_PAYMENT_TERMS_LABELS: Record<BillPaymentTerms, string> = {
  none: 'Not set (enter the due date)',
  weekly_same_friday: 'Weekly: Friday of that week',
  weekly_next_friday: 'In arrears: Friday of the following week',
};

const BILL_PAYMENT_TERMS = new Set<BillPaymentTerms>(['none', 'weekly_same_friday', 'weekly_next_friday']);

export type DirectoryAccount = SubcontractorDetails & {
  id: string;
  companyName: string;
  contactName: string | null;
  email: string | null;
  billingEmail: string | null;
  phone: string | null;
  address: string | null;
  vatRegistered: boolean;
  vatNumber: string | null;
  cisBand: CisBand;
  utrNumber: string | null;
  cisVerificationNumber: string | null;
  freeagentContactUrl: string | null;
  /** Works for Keystone under our name; can sign in to see their own CIS statements. */
  isKeystoneSubcontractor: boolean;
  /** The address they sign in to JARVIS with (fitter/labourer). */
  keystoneEmail: string | null;
  billPaymentTerms: BillPaymentTerms;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type DirectoryAccountInput = {
  companyName: string;
  contactName?: string | null;
  email?: string | null;
  billingEmail?: string | null;
  phone?: string | null;
  address?: string | null;
  vatRegistered?: boolean;
  vatNumber?: string | null;
  cisBand?: CisBand;
  utrNumber?: string | null;
  cisVerificationNumber?: string | null;
  notes?: string | null;
  isActive?: boolean;
  isKeystoneSubcontractor?: boolean;
  keystoneEmail?: string | null;
  billPaymentTerms?: BillPaymentTerms;
} & Partial<SubcontractorDetails>;

const SUBCONTRACTOR_COLUMNS: Record<keyof SubcontractorDetails, string> = {
  niNumber: 'ni_number',
  bankName: 'bank_name',
  bankSortCode: 'bank_sort_code',
  bankAccountNumber: 'bank_account_number',
  bankAccountName: 'bank_account_name',
  logoUrl: 'logo_url',
};

const CIS_BANDS = new Set<CisBand>(['none', 'gross', 'standard', 'higher']);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function text(value: unknown): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

/** UTRs are 10 digits. People paste them with spaces. */
export function normaliseUtr(value: string | null | undefined): string | null {
  const raw = text(value);
  return raw ? raw.replace(/\s+/g, '') : null;
}

/** CIS verification numbers are V + 10 digits + up to 2 letters, stored upper case. */
export function normaliseVerificationNumber(value: string | null | undefined): string | null {
  const raw = text(value);
  return raw ? raw.replace(/\s+/g, '').toUpperCase() : null;
}

export function fromDbAccount(row: Record<string, any>): DirectoryAccount {
  const cisBand = CIS_BANDS.has(row.cis_band) ? row.cis_band as CisBand : 'none';
  return {
    id: String(row.id),
    companyName: String(row.company_name || ''),
    contactName: text(row.contact_name),
    email: text(row.email),
    billingEmail: text(row.billing_email),
    phone: text(row.phone),
    address: text(row.address),
    vatRegistered: !!row.vat_registered,
    vatNumber: text(row.vat_number),
    cisBand,
    utrNumber: text(row.utr_number),
    cisVerificationNumber: text(row.cis_verification_number),
    freeagentContactUrl: text(row.freeagent_contact_url),
    isKeystoneSubcontractor: !!row.is_keystone_subcontractor,
    keystoneEmail: text(row.keystone_email),
    billPaymentTerms: BILL_PAYMENT_TERMS.has(row.bill_payment_terms) ? row.bill_payment_terms : 'none',
    notes: text(row.notes),
    isActive: row.is_active !== false,
    niNumber: text(row.ni_number),
    bankName: text(row.bank_name),
    bankSortCode: text(row.bank_sort_code),
    bankAccountNumber: text(row.bank_account_number),
    bankAccountName: text(row.bank_account_name),
    logoUrl: text(row.logo_url),
    createdAt: String(row.created_at || ''),
    updatedAt: String(row.updated_at || ''),
  };
}

export function toDbAccount(input: DirectoryAccountInput): Record<string, unknown> {
  const cisBand = input.cisBand && CIS_BANDS.has(input.cisBand) ? input.cisBand : 'none';
  // Subcontractor details are written only when the form supplies them, so other callers never blank them.
  const details: Record<string, string | null> = {};
  for (const [key, column] of Object.entries(SUBCONTRACTOR_COLUMNS)) {
    const value = input[key as keyof SubcontractorDetails];
    if (value !== undefined) details[column] = text(value);
  }
  return {
    ...details,
    company_name: String(input.companyName || '').trim(),
    contact_name: text(input.contactName),
    email: text(input.email)?.toLowerCase() ?? null,
    billing_email: text(input.billingEmail)?.toLowerCase() ?? null,
    phone: text(input.phone),
    address: text(input.address),
    vat_registered: !!input.vatRegistered,
    vat_number: input.vatRegistered ? text(input.vatNumber) : null,
    cis_band: cisBand,
    utr_number: normaliseUtr(input.utrNumber),
    cis_verification_number: cisBand === 'none' ? null : normaliseVerificationNumber(input.cisVerificationNumber),
    notes: text(input.notes),
    is_active: input.isActive !== false,
    is_keystone_subcontractor: !!input.isKeystoneSubcontractor,
    keystone_email: input.isKeystoneSubcontractor ? text(input.keystoneEmail)?.toLowerCase() ?? null : null,
    ...(input.billPaymentTerms !== undefined
      ? { bill_payment_terms: BILL_PAYMENT_TERMS.has(input.billPaymentTerms) ? input.billPaymentTerms : 'none' }
      : {}),
  };
}

/** Returns the first problem with the input, or null when it can be saved. */
export function validateAccountInput(input: DirectoryAccountInput): string | null {
  if (!String(input.companyName || '').trim()) return 'Enter a company or trading name.';
  const email = text(input.email);
  if (email && !EMAIL_PATTERN.test(email)) return 'The email address does not look right.';
  const billingEmail = text(input.billingEmail);
  if (billingEmail && !EMAIL_PATTERN.test(billingEmail)) return 'The billing email address does not look right.';
  const keystoneEmail = input.isKeystoneSubcontractor ? text(input.keystoneEmail) : null;
  if (keystoneEmail && !EMAIL_PATTERN.test(keystoneEmail)) return 'The Keystone email address does not look right.';
  const utr = normaliseUtr(input.utrNumber);
  if (utr && !/^[0-9]{10}$/.test(utr)) return 'A UTR is 10 digits.';
  if (input.cisBand && input.cisBand !== 'none') {
    const verification = normaliseVerificationNumber(input.cisVerificationNumber);
    if (verification && !/^V[0-9]{10}[A-Z]{0,2}$/.test(verification)) {
      return 'A CIS verification number is V followed by 10 digits, sometimes with 1–2 letters after.';
    }
  }
  return null;
}

/** Where invoices are emailed: billing email first, then the main email. */
export function accountInvoiceEmail(account: Pick<DirectoryAccount, 'billingEmail' | 'email'>): string | null {
  return account.billingEmail || account.email || null;
}

/** Shape the invoice page and PDF expect for the bill-to block. */
export function accountAsBillToCustomer(account: DirectoryAccount): {
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
} {
  return {
    name: account.companyName,
    email: accountInvoiceEmail(account),
    phone: account.phone,
    address: account.address,
  };
}

/** Matches the account's text fields against a search term. */
export function accountMatchesSearch(account: DirectoryAccount, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return [
    account.companyName,
    account.contactName,
    account.email,
    account.billingEmail,
    account.phone,
    account.vatNumber,
  ].some((value) => String(value || '').toLowerCase().includes(needle));
}
